import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Shell } from "./components/layout/Shell.js";
import { ApplicantShell } from "./components/layout/ApplicantShell.js";
import { LoginPage } from "./components/auth/LoginPage.js";
import { Dashboard } from "./components/dashboard/Dashboard.js";
import { CandidateList } from "./components/candidates/CandidateList.js";
import { CandidateDetail } from "./components/candidates/CandidateDetail.js";
import { JobAdminList } from "./components/job-admin/JobAdminList.js";
import { JobForm } from "./components/job-admin/JobForm.js";
import { JobDetail } from "./components/jobs/JobDetail.js";
import { ApplyForm } from "./components/jobs/ApplyForm.js";
import { ApplyConfirmation } from "./components/jobs/ApplyConfirmation.js";
import { AssignmentSubmission } from "./components/assignments/AssignmentSubmission.js";
import { AssignmentList } from "./components/assignment-library/AssignmentList.js";
import { AssignmentForm } from "./components/assignment-library/AssignmentForm.js";
import { SettingsPage } from "./components/settings/SettingsPage.js";
import { EvalCriteriaList } from "./components/eval-criteria/EvalCriteriaList.js";
import { EvalCriteriaForm } from "./components/eval-criteria/EvalCriteriaForm.js";
import { DocsPage } from "./components/docs/DocsPage.js";
import { useAuthStore } from "./store/auth.js";
import { api } from "./utils/api.js";
import { DevToolbar } from "./components/dev/DevToolbar.js";
import { isClosedCandidateStage } from "@/shared/candidate-stages.js";

function AuthGate({ children }: { children: React.ReactNode }) {
  const fetchUser = useAuthStore((s) => s.fetchUser);
  const loading = useAuthStore((s) => s.loading);
  const authenticated = useAuthStore((s) => s.authenticated);
  const location = useLocation();

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center text-gray-500">Loading...</div>
    );
  }

  if (!authenticated) {
    const returnTo = encodeURIComponent(location.pathname);
    return <Navigate to={`/auth/login?returnTo=${returnTo}`} replace />;
  }

  return <>{children}</>;
}

interface MyApp {
  jobSlug: string;
  stage: string;
}

function ApplicantHome() {
  const navigate = useNavigate();
  const logout = useAuthStore((s) => s.logout);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    api
      .get<{ results: MyApp[] }>("/applications/mine")
      .then((d) => {
        const active = d.results.find((a) => !isClosedCandidateStage(a.stage));
        if (active) {
          navigate(`/jobs/${active.jobSlug}`, { replace: true });
        } else {
          setChecked(true);
        }
      })
      .catch(() => setChecked(true));
  }, [navigate]);

  if (!checked) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center text-gray-400">Loading...</div>
    );
  }

  return (
    <div className="py-16 text-center">
      <h1 className="text-xl font-semibold text-gray-900">Mantle DC Careers</h1>
      <p className="mt-3 text-sm text-gray-500">
        Use the link from the job posting to view the position and apply.
      </p>
      <p className="mt-6 text-xs text-gray-400">
        <button onClick={logout} className="text-gray-500 underline hover:text-gray-700">
          Sign out
        </button>
      </p>
    </div>
  );
}

function RoleHome() {
  const user = useAuthStore((s) => s.user);
  if (user?.role === "employee") return <Navigate to="/dashboard" replace />;
  return <ApplicantHome />;
}

export default function App() {
  return (
    <BrowserRouter>
      <DevToolbar />
      <Routes>
        <Route path="/auth/login" element={<LoginPage />} />

        {/* Employee routes — sidebar layout */}
        <Route
          element={
            <AuthGate>
              <Shell />
            </AuthGate>
          }
        >
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="candidates" element={<CandidateList />} />
          <Route path="candidates/:id" element={<CandidateDetail />} />
          <Route path="jobs/manage" element={<JobAdminList />} />
          <Route path="jobs/new" element={<JobForm />} />
          <Route path="jobs/:slug/edit" element={<JobForm />} />
          <Route path="assignments" element={<AssignmentList />} />
          <Route path="assignments/new" element={<AssignmentForm />} />
          <Route path="assignments/:id/edit" element={<AssignmentForm />} />
          <Route path="eval-criteria" element={<EvalCriteriaList />} />
          <Route path="eval-criteria/new" element={<EvalCriteriaForm />} />
          <Route path="eval-criteria/:id/edit" element={<EvalCriteriaForm />} />
          <Route path="docs" element={<DocsPage />} />
          <Route path="settings" element={<SettingsPage />} />
        </Route>

        {/* Applicant/job routes — minimal header layout */}
        <Route
          element={
            <AuthGate>
              <ApplicantShell />
            </AuthGate>
          }
        >
          <Route index element={<RoleHome />} />
          <Route path="jobs/:slug" element={<JobDetail />} />
          <Route path="jobs/:slug/apply" element={<ApplyForm />} />
          <Route path="jobs/:slug/confirmation" element={<ApplyConfirmation />} />
          <Route path="jobs/:slug/assignment/:candidateId" element={<AssignmentSubmission />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
